import { ref, computed } from 'vue'
import axios from 'axios'

const user = ref(JSON.parse(localStorage.getItem('user')) || null)
const token = ref(localStorage.getItem('token') || null)

const isAuthenticated = computed(() => !!token.value)

export function useAuth() {
  // Set axios defaults
  // axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL || '/api'
  if (token.value) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
  }

  const login = async (credentials) => {
    try {
      const response = await axios.post('/api/login', credentials)

      if (response.data.success) {
        const { user: userData, token: tokenData, permissions } = response.data.data

        // Attach permissions to user object
        if (permissions) {
          userData.permissions = permissions;
        }

        user.value = userData
        token.value = tokenData

        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(userData))
        localStorage.setItem('token', tokenData)

        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`

        return { success: true, message: response.data.message }
      }
    } catch (error) {
      let message = 'Erreur de connexion'
      let details = null

      if (error.response?.data) {
        message = error.response.data.message || message
        // For validation errors, Laravel returns errors in the format {"email": ["error message"]}
        if (error.response.data.errors) {
          details = error.response.data.errors
        }
      }

      return {
        success: false,
        message,
        details
      }
    }
  }

  const logout = async (redirect = true) => {
    try {
      if (token.value) {
        await axios.post('/api/logout')
      }
    } catch (error) {
      // Ignore logout errors
    } finally {
      // Clear local storage regardless of API success
      user.value = null
      token.value = null
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      delete axios.defaults.headers.common['Authorization']

      if (redirect) {
        window.location.href = '/login'
      }
    }
  }

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/register', userData)

      if (response.data.success) {
        const { user: newUser, token: tokenData } = response.data.data
        user.value = newUser
        token.value = tokenData

        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(newUser))
        localStorage.setItem('token', tokenData)

        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`

        return { success: true, message: response.data.message }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur d\'inscription'
      }
    }
  }

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/user', profileData)

      if (response.data.success) {
        const updatedUser = response.data.data
        user.value = updatedUser
        localStorage.setItem('user', JSON.stringify(updatedUser))

        return { success: true, message: response.data.message }
      }
    } catch (error) {
      let message = 'Erreur lors de la mise à jour du profil'
      let errors = null

      if (error.response?.data) {
        message = error.response.data.message || message
        if (error.response.data.errors) {
          errors = error.response.data.errors
        }
      }

      return {
        success: false,
        message,
        errors
      }
    }
  }

  const fetchUser = async () => {
    if (!token.value) return

    try {
      const response = await axios.get('/api/user')
      if (response.data.success) {
        user.value = response.data.data
        // Permissions are usually sent with user login, but if we fetch user here, 
        // we assume the backend appends permissions or we should request them.
        // My AuthController::user updates DO append permissions.
        if (response.data.permissions) {
          user.value.permissions = response.data.permissions;
        }
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    } catch (error) {
      // Token might be expired
      logout()
    }
  }

  // Check if user has role
  const hasRole = (role) => {
    return user.value?.role === role
  }

  // Check if user has permission
  const hasPermission = (permission) => {
    if (user.value?.role === 'superadmin') return true; // Superadmin has all permissions
    return user.value?.permissions?.includes(permission) || false;
  }

  const isSuperAdmin = computed(() => hasRole('superadmin'))
  const isAdmin = computed(() => hasRole('admin') || hasRole('superadmin'))
  const isDirector = computed(() => hasRole('directeur'))
  const isCostController = computed(() => hasRole('cost_controller'))
  const isHR = computed(() => hasRole('rh'))
  const isPointeur = computed(() => hasRole('pointeur'))
  const isSupervisor = computed(() => hasRole('conducteur_travaux'))
  const isUser = computed(() => hasRole('user'))
  const isQhsePays = computed(() => isAdmin.value || hasRole('qhse_pays'))
  const isQhseChantier = computed(() => isAdmin.value || hasRole('qhse_chantier'))
  const isRelaisQhse = computed(() => isAdmin.value || hasRole('relais_qhse'))
  const isQhseUser = computed(() => isAdmin.value || isQhsePays.value || isQhseChantier.value || isRelaisQhse.value)

  return {
    user: computed(() => user.value),
    token: computed(() => token.value),
    isAuthenticated,
    isSuperAdmin,
    isAdmin,
    isDirector,
    isCostController,
    isHR,
    isPointeur,
    isSupervisor,
    isUser,
    isQhsePays,
    isQhseChantier,
    isRelaisQhse,
    isQhseUser,
    login,
    logout,
    register,
    updateProfile,
    fetchUser,
    hasRole,
    hasPermission
  }
}
